import * as R from "ramda";
import * as React from "react";
import { FaDownload } from "react-icons/fa";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import {
  ButtonRow,
  CheckboxDescription,
  CustomSearchInput,
  FieldSet,
  FooterNote,
  FormSelect,
  ResetButton,
  SearchField,
  SetupButtonLink,
} from "../../../../App/Common/Components/FormComponents";
import { Warning, WarningHeader, WarningMessage } from "../../../../App/Common/Styles/Alert";
import { Section, SectionSubTitle, SectionTitle } from "../../../../App/Common/Styles/containers";
import { IState } from "../../../../App/reducer";
import { LMSMap, VersionMap } from "../../../../App/ScormPackageManagement/utils";
import { config } from "../../../../config";
import { IOrganization } from "../../../../reducers/course-author/types";
import { contains } from "../../../../utils";
import { fullCourseViews } from "../../../../utils/courseViews";
import { getDownloadUrl, InputNames, ISCORMExportState } from "../../../course-author/helpers/scorm-export";
import { IFullCourse } from "../../../course-author/types";
import AutoComplete from "../Autocomplete";
import CheckBox from "../Checkbox";
import { FormContainer, Input } from "../FormElements";
import { api } from "../../../../dependencies";

const HorizontalSpan = styled.span`
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
`;

const PackageNameInput = styled(Input)`
  border: none;
  border-bottom: 0.1rem solid black;
  background-color: inherit;
  font-size: 1.6rem;
  color: ${({ theme }) => theme.colors.darkblue};
  :hover {
    border-bottom: 0.2rem solid black;
  }

  :focus {
    border-bottom: 0.1rem solid black;
  }
`;

const separateContent = css`
  margin: 1rem 0;
`;

const CourseParameterFormContainer = styled(FormContainer)`
  ${separateContent};
`;

const AdvancedFormContainer = styled(FormContainer)`
  ${separateContent};
`;

const SpacedNote = styled.div`
  font-weight: 600;
  font-size: 1.2rem;
  font-style: italic;
  ${separateContent};
`;

const SCORMNameFieldSet = styled(FieldSet)`
  ${separateContent};
`;

const SpacedContainer = styled(FormContainer)`
  ${separateContent};
`;

export interface ISCORMExportStateProps {
  organizations: IOrganization[];
}

export interface ISCORMExportOwnProps {
  course: IFullCourse;
  courseId: string;
  userId: string;
}

export type ISCORMExportProps = ISCORMExportOwnProps & ISCORMExportStateProps;

const LMSOptions = Object.keys(LMSMap).map(key => ({
  name: LMSMap[key],
  value: key,
}));

const SCORMVersions = Object.keys(VersionMap).map(key => ({
  name: VersionMap[key],
  value: key,
}));

const SCORMModes = [
  { name: "Standard", value: "standard" },
  { name: "Strict", value: "strict" },
];

const containsValidPublication = (course: IFullCourse): boolean => {
  return course.state.publications.elems.filter(({ deleted }) => !deleted).length > 0;
};

export class SCORMExport extends React.Component<ISCORMExportProps, ISCORMExportState> {
  private refObjects: {
    [InputNames.PackageName]: React.RefObject<HTMLInputElement>;
    [InputNames.SelectLMS]: React.RefObject<HTMLSelectElement>;
    [InputNames.SelectSCORMVersion]: React.RefObject<HTMLSelectElement>;
    [InputNames.SetParameters]: React.RefObject<HTMLSelectElement>;
  };

  private defaultAdvancedSettings = {
    [InputNames.HideNavbar]: false,
    [InputNames.SelectLMS]: LMSOptions[0].value,
    [InputNames.SelectSCORMVersion]: SCORMVersions[0].value,
  };

  constructor(props: ISCORMExportProps) {
    super(props);
    this.refObjects = {
      [InputNames.PackageName]: React.createRef(),
      [InputNames.SelectLMS]: React.createRef(),
      [InputNames.SelectSCORMVersion]: React.createRef(),
      [InputNames.SetParameters]: React.createRef(),
    };
    const ownerOrg =
      R.find(org => org.organizationId === props.course.state.owner, props.organizations) || ({} as IOrganization);
    this.state = {
      formValues: {
        [InputNames.AdaptiveBranding]: true,
        [InputNames.AllowReview]: true,
        [InputNames.ChooseOrganization]: ownerOrg.displayName || "",
        [InputNames.HideNavbar]: false,
        [InputNames.PackageName]: fullCourseViews.masterLocaleTitle(props.course) || "",
        [InputNames.SelectLMS]: LMSOptions[0].value,
        [InputNames.SelectSCORMVersion]: SCORMVersions[0].value,
        [InputNames.SetParameters]: "standard",
        [InputNames.SelectedOrgId]: props.course.state.owner,
      },
      ownerOrg,
    };
  }

  public componentDidMount() {
    this.applyScormSettings();
  }

  public toggleBooleanFormValue = (field: InputNames) => () =>
    this.setState({
      formValues: {
        ...this.state.formValues,
        [field]: !this.state.formValues[field],
      },
    });
  public changeInputValue = (field: InputNames) => () => {
    const { formValues } = this.state;
    const ref: React.RefObject<HTMLInputElement> = this.refObjects[field];
    if (!R.isNil(ref)) {
      this.setState({
        formValues: {
          ...formValues,
          [field]: ref.current ? ref.current.value : formValues[field],
        },
      });
    }
  };
  public selectValue = (field: InputNames) => () => {
    const { formValues } = this.state;
    const ref: React.RefObject<HTMLSelectElement> = this.refObjects[field];
    if (!R.isNil(ref)) {
      this.setState({
        formValues: {
          ...formValues,
          [field]: ref.current ? ref.current.value : formValues[field],
        },
      });
    }
  };
  public resetAdvancedSettings = () => {
    this.setState({
      formValues: {
        ...this.state.formValues,
        ...this.defaultAdvancedSettings,
      },
    });
  };

  public render() {
    return (
      <Section>
        <SectionTitle>SCORM Export</SectionTitle>
        {!containsValidPublication(this.props.course) && (
          <Warning>
            <WarningHeader>Warning</WarningHeader>
            <WarningMessage>
              This course has not been published yet. Learners will not be able to take this course unless it's been
              published.
            </WarningMessage>
          </Warning>
        )}
        <FormContainer>
          <SCORMNameFieldSet>
            <legend>SCORM Package Name</legend>
            <PackageNameInput
              ref={this.refObjects[InputNames.PackageName]}
              name={InputNames.PackageName}
              value={this.state.formValues[InputNames.PackageName] as string}
              onChange={this.changeInputValue(InputNames.PackageName)}
              placeholder="Package name"
            />
          </SCORMNameFieldSet>

          <SearchField legend={"Target Organisation"}>
            <AutoComplete
              fetchSuggestions={this.changeChooseOrgValue}
              htmlName={InputNames.ChooseOrganization}
              initialValue={this.state.formValues[InputNames.ChooseOrganization] as string}
              items={this.filterOrganizations()}
              itemValue={R.propOr("", "displayName")}
              placeholderText="Type here to look for an Organisation"
              setSelected={this.selectOrganization}
              renderInputComponent={CustomSearchInput}
            />
          </SearchField>

          <CourseParameterFormContainer>
            <SectionSubTitle>Set course parameter</SectionSubTitle>
            <FormSelect
              ref={this.refObjects[InputNames.SetParameters]}
              name={InputNames.SetParameters}
              value={this.state.formValues[InputNames.SetParameters] as string}
              onChange={this.selectValue(InputNames.SetParameters)}
            >
              {SCORMModes.map(({ value, name }, i) => (
                <option key={i} value={value}>
                  {name}
                </option>
              ))}
            </FormSelect>
            <SpacedNote>
              <p>
                Some units allow learners to bypass the lesson if they pass the assessment. This allows learners to
                "test out" of a unit by demonstrating that they already understand its content. This can be overridden
                by the mode in which a course is taken.
              </p>
              <p>In "STANDARD" mode, learners will be able to complete such a unit by passing the assessment.</p>
              <p>
                In "STRICT" mode, learners will be unable to complete a unit by skipping to the assessment, even if this
                has been allowed by the course author.
              </p>
            </SpacedNote>

            <HorizontalSpan>
              {this.checkbox(InputNames.AdaptiveBranding, `Include Salt ADAPTIVE branding`)}
            </HorizontalSpan>
            <FooterNote>
              If enabled, a small message reading "A course by Salt Adaptive" will be added to the footer of the course
              completion page.
            </FooterNote>

            <HorizontalSpan>{this.checkbox(InputNames.AllowReview, `Allow course review`)}</HorizontalSpan>
            <FooterNote>
              If enabled, the learner will be able to re-enter the course after it is complete to review the materials.
            </FooterNote>
          </CourseParameterFormContainer>

          <AdvancedFormContainer>
            <SectionSubTitle>Advanced Settings</SectionSubTitle>
            <SpacedContainer>
              <label htmlFor={InputNames.SelectLMS}>LMS</label>
              <FormSelect
                ref={this.refObjects[InputNames.SelectLMS]}
                value={this.state.formValues[InputNames.SelectLMS] as string}
                name={InputNames.SelectLMS}
                onChange={this.selectValue(InputNames.SelectLMS)}
              >
                {LMSOptions.map(({ value, name }, i) => (
                  <option key={i} value={value}>
                    {name}
                  </option>
                ))}
              </FormSelect>
            </SpacedContainer>

            <SpacedContainer>
              <label htmlFor={InputNames.SelectSCORMVersion}>SCORM Version</label>
              <FormSelect
                name={InputNames.SelectSCORMVersion}
                ref={this.refObjects[InputNames.SelectSCORMVersion]}
                value={this.state.formValues[InputNames.SelectSCORMVersion] as string}
                onChange={this.selectValue(InputNames.SelectSCORMVersion)}
              >
                {SCORMVersions.map(({ value, name }, i) => (
                  <option key={i} value={value}>
                    {name}
                  </option>
                ))}
              </FormSelect>
            </SpacedContainer>

            <ButtonRow margin={1} width={17}>
              <ResetButton onClick={this.resetAdvancedSettings}>Reset Advanced Settings</ResetButton>
            </ButtonRow>

            <SpacedNote>
              <p>
                SCORM 1.2 is better supported among LMSes and robust. However, it is not as feature rich as SCORM 2004.
              </p>
              <p>
                Using SCORM 2004 3rd edition allows us to ensure that the progressing/resetting of progress and
                completion information is more complete.
              </p>
            </SpacedNote>

            <HorizontalSpan>{this.checkbox(InputNames.HideNavbar, `Hide LMS Navigation bar`)}</HorizontalSpan>
            <FooterNote>
              Enable this option to hide the navigation bar which removes the progress bar, course outline and exit
              buttons.
            </FooterNote>
          </AdvancedFormContainer>
        </FormContainer>

        <ButtonRow margin={1} width={25}>
          <SetupButtonLink href={this.masterDownload()} download={true}>
            <span>
              <FaDownload /> Download SCORM file
            </span>
          </SetupButtonLink>
        </ButtonRow>
      </Section>
    );
  }

  private changeChooseOrgValue = ({ value }: { value: string }) => {
    this.setState({
      formValues: {
        ...this.state.formValues,
        [InputNames.ChooseOrganization]: value,
      },
    });
  };

  private selectOrganization = async (org: IOrganization) => {
    this.changeChooseOrgValue({ value: org.displayName });
    await this.setState({
      formValues: {
        ...this.state.formValues,
        [InputNames.SelectedOrgId]: org.organizationId,
      },
    });
    this.applyScormSettings();
  };

  private filterOrganizations = (): IOrganization[] => {
    const { formValues } = this.state;
    const filterFn = R.compose<IOrganization[], IOrganization[], IOrganization[]>(
      list => list.slice(0, 50),
      R.filter((org: IOrganization) => contains(formValues[InputNames.ChooseOrganization] as string, org.displayName))
    );
    return filterFn(this.props.organizations);
  };

  private masterDownload = () => {
    const { courseId, userId } = this.props;
    const { formValues } = this.state;
    return getDownloadUrl(config.saltcomplianceApiRoot, courseId, userId, formValues);
  };

  private applyScormSettings = async () => {
    const userId = this.props.userId;
    const activeOrg = this.state.formValues[InputNames.SelectedOrgId].toString();
    const [resolveErr, resolveRes] = await api.resolve.organization.post(userId, [activeOrg]);
    if (resolveErr || !resolveRes) {
      return;
    }

    const response = resolveRes;
    const scormSettings = response.data[0].value.state.scormSettings;
    const scormVersion = scormSettings.scormVersion;
    const lms = scormSettings.lms;
    const hideNavBar = scormSettings.hideNavBar;

    this.setState(prevState => ({
      formValues: {
        ...prevState.formValues,
        [InputNames.SelectSCORMVersion]: scormVersion || SCORMVersions[0].value,
        [InputNames.SelectLMS]: lms || LMSOptions[0].value,
        [InputNames.HideNavbar]: hideNavBar || false,
      },
    }));
  };

  private checkbox = (field: InputNames, label: string) => (
    <label>
      <CheckBox customChecked={this.state.formValues[field] as boolean} onChange={this.toggleBooleanFormValue(field)} />
      <CheckboxDescription>{label}</CheckboxDescription>
    </label>
  );
}

export default connect<ISCORMExportStateProps, {}, ISCORMExportOwnProps>(
  (state: IState, ownProps: ISCORMExportOwnProps) => ({
    organizations: state.App.CourseAuthor._.organizations || [],
  })
)(SCORMExport);
